import { Card, Space, Tag, Input, Row, Col, Popconfirm, Divider, Form, Button, Select, message, Spin } from "antd";
import { CloseCircleFilled, DeleteOutlined } from "@ant-design/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "../../context";
import { languageOptions } from "./languageOptions";
import TextArea from "antd/lib/input/TextArea";

const AudioTracksList = ({ existingAudioTracks, handleDeleteTrack }) => {

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      
      {/* Table Header */}
      <Row
        align="middle"
        style={{
          fontWeight: "bold",
          background: "#fafafa",
          padding: "8px 12px",
          borderRadius: "8px",
        }}
      >
        <Col span={8}>Language Name</Col>
        <Col span={8} style={{ textAlign: "center" }}>Status</Col>
        <Col span={8} style={{ textAlign: "center" }}>Actions</Col>
      </Row>

      {/* Table Body */}
      {existingAudioTracks.map((track) => (
        <Row
          key={track.id}
          align="middle"
          style={{
            marginBottom: 8,
            padding: "8px 12px",
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
          }}
        >
          <Col span={8}>
            <span>{track.languageName}</span>
          </Col>

          <Col span={8} style={{ textAlign: "center" }}>
            <Tag color="blue" style={{ textTransform: "capitalize" }}>
              {track.status}
            </Tag>
          </Col>

          <Col span={8} style={{ textAlign: "center" }}>
            <Space>
              <Popconfirm
                placement="top"
                title={`Do you want to delete ${track.languageName} audio track?`}
                onConfirm={() => handleDeleteTrack(track.id)}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
                icon={null}
              >
                <DeleteOutlined
                  style={{ fontSize: "16px", color: "red", cursor: "pointer" }}
                />
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      ))}
    </Space>
  );
};

const AudioTracks = ({ selectedContent }) => {
  const [audioTrackForm] = Form.useForm();
  const [addingAudioTrack, setAddingAudioTrack] = useState(false);
  const [editingAudioTrack, setEditingAudioTrack] = useState(null);
  const [existingAudioTracks, setExistingAudioTracks] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token, apiUrl } = useContext(Context);

  useEffect(() => {
    const audioTracks = selectedContent?.audioTracksFp?.length >= 1 ? selectedContent?.audioTracksFp : null;
    setExistingAudioTracks(audioTracks);
  }, [selectedContent]);

  const fetchTracks = useCallback(async () => {

    setLoading(true);
    setError(null);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const params = new URLSearchParams({
        content_type: selectedContent?.contentType,
        track_type: "audio",
      });

      if (selectedContent.contentType === "movie" && selectedContent.contentId) {
        params.append("content_id", selectedContent.contentId);
      }

      if (selectedContent.contentType === "episode" && selectedContent.seriesId && selectedContent.season && selectedContent.number) {
        params.append("series_id", selectedContent.seriesId);
        params.append("season", selectedContent.season);
        params.append("episode", selectedContent.number);
      }

      const res = await fetch(
        `${apiUrl}/fpvideo/get_tracks?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to fetch tracks");
      }

      const data = await res.json();
      const fieldName = "audioTracksFp"

      setExistingAudioTracks(data[fieldName] || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, selectedContent, token]);

  // Handle audio track addition/editing
  const handleAudioTrackSubmit = async (values) => {
    if (!selectedContent) {
      message.error('Please select content first');
      return;
    }

    try {
      setAddingAudioTrack(true);

      const selectedLanguage = languageOptions.find(option => option.value === values.audioLanguage);

      const payload = {
        contentId: selectedContent.contentId,
        mediaId: selectedContent.mediaIdFp,
        url: values.audioUrl,
        languageCode: values.audioLanguage,
        languageName: selectedLanguage.label,
        type: "audio",
        isEdit: !!editingAudioTrack,
      };

      if (payload.isEdit) {
        payload.trackId = editingAudioTrack?.id
      }

      const response = await fetch(`${apiUrl}/fpvideo/manage_tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        message.success(editingAudioTrack ? 'Audio track updated successfully!' : 'Audio track added successfully!');
        audioTrackForm.resetFields();
        setEditingAudioTrack(null);
        fetchTracks();

      } else {

        message.error(result.error.error.description || result.error.error.message || "Failed to add audio track");

      }
    } catch (error) {

      message.error('Network error: Failed to manage audio track. Please check your connection and try again.');

    } finally {
      setAddingAudioTrack(false);
    }
  };

  // Handle track deletion
  const handleDeleteTrack = async (trackId) => {

    try {

      const requestBody = {
        content_type: selectedContent.contentType,
        content_id: selectedContent.key,
      };

      if (selectedContent.contentType === "episode") {
        requestBody.series_id = selectedContent.seriesId;
        requestBody.season_number = selectedContent.seasonNumber;
        requestBody.episode_number = selectedContent.episodeNumber;
      }

      const response = await fetch(
        `${apiUrl}/fpvideo/delete_track/${selectedContent.mediaIdFp}/${trackId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();
      if (result.success) {

        message.success('Audio track deleted successfully!');
        fetchTracks();
      } else {
        message.error(result.error.error.description || result.error.error.message || 'Failed to delete audio track');

      }
    } catch (error) {

      message.error('Network error: Failed to delete audio track. Please check your connection and try again.');

    }
  };

  return (
    <Card title="Audio Tracks" style={{ width: 500 }}>
      <Form
        form={audioTrackForm}
        onFinish={handleAudioTrackSubmit}
        layout="vertical"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>

          <Col span={12}>
            <Form.Item
              name="audioLanguage"
              label="Language"
              rules={[{ required: true, message: 'Please select language' }]}
            >
              <Select
                placeholder="Select language"
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                disabled={addingAudioTrack}
              >
                {languageOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="audioUrl"
              label="Audio URL"
              rules={[
                { required: true, message: 'Please enter audio URL' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input
                placeholder="https://example.com/audio.mp3"
                disabled={addingAudioTrack}
              />
            </Form.Item>
          </Col>

        </Row>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={addingAudioTrack}
              disabled={addingAudioTrack || loading || !selectedContent?.mediaIdFp}
            >
              Add Audio Track
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Divider />

      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin tip="Loading audio tracks..." />
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", color: "red" }}>
          {error}
        </div>
      ) : existingAudioTracks && existingAudioTracks.length > 0 ? (
        <AudioTracksList existingAudioTracks={existingAudioTracks} handleDeleteTrack={handleDeleteTrack} />
      ) : (
        <div style={{ textAlign: "center", color: "#999" }}>
          No audio tracks available
        </div>
      )}

    </Card>
  );
};

export default AudioTracks;
